/**
 * Dynamic Type for the page: a thin layer over the layout engine's font
 * resolution (`src/layout/typography.ts`), which is the single source of
 * truth for Apple's Dynamic Type table (point sizes per text style and size
 * category) and iOS line heights.
 *
 * The page writes one CSS custom property pair per text style on the device
 * frame (`--sb-ts-<style>-size` / `-lh`) plus a `-scale` factor relative to
 * Large, so styled text that is not laid out by the engine (chrome, the mock
 * page before the first layout) still follows the selected size.
 */

import {
  TEXT_STYLE_SIZES,
  lineHeightFor,
  textStyleSize,
  type TextStyle,
} from './layout/typography';
import type { DynamicTypeSize, Platform } from './protocol';

export {
  DYNAMIC_TYPE_SIZES,
  FONT_FAMILIES,
  FONT_WEIGHTS,
  LINE_HEIGHT_RATIO,
  LEADINGS,
  leadingFor,
  textHeight,
  TEXT_STYLE_SIZES,
  MAC_TEXT_STYLE_SIZES,
  defaultFont,
  lineHeightFor,
  makeFont,
  resolveFont,
  textStyleSize,
} from './layout/typography';
export type { TextStyle } from './layout/typography';

export const DEFAULT_TYPE_SIZE: DynamicTypeSize = 'large';

export const TEXT_STYLES: readonly TextStyle[] = Object.keys(TEXT_STYLE_SIZES) as TextStyle[];

/** Default weight of a text style (only `headline` differs from regular). */
export const TEXT_STYLE_WEIGHT: Partial<Record<TextStyle, 'semibold'>> = {
  headline: 'semibold',
};

export interface ResolvedTextStyle {
  fontSize: number;
  lineHeight: number;
}

/** Point size and line height of `style` at `size` (on the Mac, its one size). */
export function resolveTextStyle(style: TextStyle, size: DynamicTypeSize, platform: Platform = 'ios'): ResolvedTextStyle {
  const fontSize = textStyleSize(style, size, platform);
  return { fontSize, lineHeight: lineHeightFor(fontSize) };
}

/** Scale factor of `style` at `size` relative to Large (`Font.system(size:relativeTo:)`); 1 on the Mac. */
export function relativeScale(style: TextStyle, size: DynamicTypeSize, platform: Platform = 'ios'): number {
  return textStyleSize(style, size, platform) / textStyleSize(style, DEFAULT_TYPE_SIZE, platform);
}

/** CSS custom property names the renderer reads for a text style. */
export function textStyleVar(style: TextStyle, metric: 'size' | 'lh'): string {
  return `--sb-ts-${style}-${metric}`;
}

/**
 * Write every text style's size and line height for `size` as CSS custom
 * properties on `target` (the device frame).
 */
export function applyTypeSizeVariables(target: HTMLElement, size: DynamicTypeSize, platform: Platform = 'ios'): void {
  for (const style of TEXT_STYLES) {
    const { fontSize, lineHeight } = resolveTextStyle(style, size, platform);
    target.style.setProperty(textStyleVar(style, 'size'), `${fontSize}px`);
    target.style.setProperty(textStyleVar(style, 'lh'), `${Math.round(lineHeight * 100) / 100}px`);
    target.style.setProperty(`--sb-ts-${style}-scale`, String(relativeScale(style, size, platform)));
  }
}
